'use client';

import {
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import type { OptionKey } from '@/lib/options/optionTypes';
import { hasPublicationExtendedFiltersSet, usePublicationFilterStore } from '@/stores/publicationFilterStore';
import { PrivilegeEnum, type PublicationSearchCriteriaDto } from '@/api/ApiClient';
import { useDialogStore } from '@/stores/dialogStore';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useReopenSearchPanelOnFailure } from '@/hooks/useReopenSearchPanelOnFailure';
import { markNextSearchUserInitiated } from '@/stores/searchUiStore';

const OPTION_KEYS: OptionKey[] = ['materials', 'publicationType', 'scienceDrivers'];

type PublicationSearchPanelProps = {
  /** When true, the title/action header row is omitted (e.g. iframe embed). */
  hideTitleRow?: boolean;
  /** Called after a publication is created from this panel. */
  onPublicationCreated?: () => void;
};

export function PublicationSearchPanel({ hideTitleRow = false, onPublicationCreated }: PublicationSearchPanelProps) {
  const { options, loading, error } = useSelectOptions(OPTION_KEYS);
  const { showCreatePublicationDialog } = useDialogStore();
  const { isAuthorized } = usePrivileges();
  const { filters, setFilters, resetFilters, isSearching, setPaginationModel } = usePublicationFilterStore();
  const canCreatePublication = isAuthorized(PrivilegeEnum.Publication, 'create');
  const [filterDraft, setFilterDraft] = useState<PublicationSearchCriteriaDto | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  useReopenSearchPanelOnFailure(setIsExpanded);
  const localFilters = filterDraft ?? filters;
  const scienceDrivers = options.scienceDrivers || [];

  const setLocalFilters = useCallback(
    (next: PublicationSearchCriteriaDto | ((prev: PublicationSearchCriteriaDto) => PublicationSearchCriteriaDto)) => {
      setFilterDraft((prevDraft) => {
        const base = prevDraft ?? usePublicationFilterStore.getState().filters;
        return typeof next === 'function'
          ? (next as (p: PublicationSearchCriteriaDto) => PublicationSearchCriteriaDto)(base)
          : next;
      });
    },
    []
  );

  const selectedMaterials = useMemo(() => {
    const ids = new Set(localFilters.materials || []);
    return (options.materials || []).filter((m) => m.id !== undefined && ids.has(m.id));
  }, [localFilters.materials, options.materials]);

  const materialOptions = useMemo(() => {
    const seen = new Set<number>();
    return (options.materials || []).filter((m) => {
      if (m.id === undefined || m.id === null) return false;
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [options.materials]);

  const apply = useCallback(() => {
    markNextSearchUserInitiated();
    setFilters(localFilters);
    setFilterDraft(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsExpanded(false);
  }, [localFilters, setFilters, setPaginationModel]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (isSearching) return;
      apply();
    },
    [apply, isSearching]
  );

  const reset = useCallback(() => {
    setFilterDraft(null);
    resetFilters();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [resetFilters, setPaginationModel]);

  const handleCreatePublication = useCallback(async () => {
    const result = await showCreatePublicationDialog();
    if (result) {
      onPublicationCreated?.();
    }
  }, [onPublicationCreated, showCreatePublicationDialog]);

  if (loading) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body1">Loading filter options...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1 }}>
        <Typography variant="body1" color="error">
          Error loading filter options...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 1.5 }}>
      {!hideTitleRow ? (
        <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2, mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Search Publications
          </Typography>
          <Box display="flex" gap={1} sx={{ ml: 'auto' }}>
            {canCreatePublication ? (
              <Button
                variant="contained"
                onClick={handleCreatePublication}
                size="large"
                sx={{
                  minWidth: '160px',
                  fontWeight: 500,
                }}
                startIcon={<AddIcon />}
              >
                Create Publication
              </Button>
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          mb: 1.5,
          mt: hideTitleRow ? 1 : 0,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          py: 1,
          borderRadius: 1,
        }}
        onKeyDown={handleSearchOnEnter}
      >
        <TextField
          label="Search in Title, DOI, Authors, and Description"
          value={localFilters.searchText || ''}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, searchText: e.target.value }))}
          size="small"
          sx={{ flex: 1, minWidth: 320 }}
        />
        <Badge
          color="primary"
          variant="dot"
          invisible={!hasPublicationExtendedFiltersSet(localFilters)}
          sx={{ '& .MuiBadge-badge': { right: 8, top: 8 } }}
        >
          <Button
            variant={isExpanded ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            Filters
          </Button>
        </Badge>
        <Button variant="outlined" color="error" onClick={reset} disabled={isSearching}>
          Reset Search Criteria
        </Button>
        <Button variant="contained" onClick={apply} disabled={isSearching}>
          Search
        </Button>
      </Box>
      {isExpanded ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
          <Autocomplete
            multiple
            options={materialOptions}
            sx={{ minWidth: 260, flex: 1 }}
            getOptionLabel={(option) => option.name || String(option.id || '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedMaterials}
            onChange={(_, value) =>
              setLocalFilters((prev) => ({
                ...prev,
                materials: value.map((v) => v.id).filter((id): id is number => id !== undefined),
              }))
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name || String(option.id || '')}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Materials" size="small" />}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Science Driver</InputLabel>
            <Select
              value={localFilters.scienceDriver ?? ''}
              onChange={(e) => {
                const raw = String(e.target.value ?? '');
                setLocalFilters((prev) => ({
                  ...prev,
                  scienceDriver: raw === '' ? undefined : Number(raw),
                }));
              }}
              label="Science Driver"
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {scienceDrivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.label || driver.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Publication Type</InputLabel>
            <Select
              value={localFilters.publicationType ?? ''}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  publicationType: (e.target.value as any) || undefined,
                }))
              }
              label="Publication Type"
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {(options.publicationType || []).map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  {type.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : null}
    </Box>
  );
}
